"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Dna, ArrowRight, FlaskConical, Activity } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function BespokePeptideSynthesis() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: ctaRef,
    isVisible: isCtaVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="223191" id="year-round-property-vitality" className="bg-background text-foreground relative min-h-[90vh] flex items-center justify-center overflow-hidden border-b border-border/50">
      {}
      <div className="absolute inset-0 z-0 pointer-events-none">
        {}
        <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1668432/img/seasonal_landscape_split.png')] bg-cover bg-center opacity-20 mix-blend-multiply transform scale-105 animate-[pulse_10s_ease-in-out_infinite]" aria-hidden="true" />
        
        {}
        <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/90 to-background" />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-transparent to-background" />
        
        {}
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-primary/5 rounded-lg blur-[120px] mix-blend-multiply" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-secondary/10 rounded-lg blur-[100px] mix-blend-multiply" />
        
        {}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#14532d08_1px,transparent_1px),linear-gradient(to_bottom,#14532d08_1px,transparent_1px)] bg-[size:32px_32px]" />
      </div>

      <div className="container relative z-10 mx-auto px-4 py-24 sm:py-32 flex flex-col items-center text-center">
        
        {}
        <div ref={headerRef} className={`transition-all duration-1000 ease-out transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-accent backdrop-blur-md border border-border/50 text-accent-foreground text-sm font-medium mb-8 uppercase tracking-widest shadow-md">
            <Activity className="w-4 h-4 text-primary" />
            <span>12-Month Maintenance Care</span>
          </div>
        </div>

        {}
        <div ref={textRef} className={`transition-all duration-1000 delay-200 ease-out transform max-w-5xl ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-default font-light tracking-tighter mb-6 leading-[1.1]">
            Preserving the Beauty of <br className="hidden md:block" />
            <span className="font-semibold text-transparent bg-clip-text bg-gradient-to-r from-primary via-secondary to-primary bg-[length:200%_auto] animate-[pulse_4s_ease-in-out_infinite]">
              Every Season
            </span>
          </h1>
          
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-12 font-default font-light leading-relaxed">
            Campos V Landscaping ensures your property thrives from the lush growth of summer to the crisp clarity of fall. Our precision care adapts to the rhythm of nature for year-round vitality.
          </p>
        </div>

        {}
        <div ref={ctaRef} className={`transition-all duration-1000 delay-400 ease-out transform flex flex-col items-center w-full ${isCtaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="flex flex-col sm:flex-row gap-6 mb-16">
            <Button asChild size="lg" className="h-16 px-10 text-lg rounded-md bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg hover:shadow-xl transition-all duration-300 border border-primary/20">
              <Link to="/GetAQuotePage">
                Book Consultation <ArrowRight className="ml-3 w-5 h-5" />
              </Link>
            </Button>
            
            <Button asChild variant="outline" size="lg" className="h-16 px-10 text-lg rounded-md bg-background/40 backdrop-blur-sm border-border/50 hover:bg-accent hover:text-accent-foreground transition-all duration-300">
              <Link to="/OurCraftPage">
                Our Services
              </Link>
            </Button>
          </div>

          {}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full max-w-4xl pt-12 border-t border-border/30">
            {[{
            icon: Dna,
            title: "Organic Precision",
            desc: "Sustainable growth methods"
          }, {
            icon: FlaskConical,
            title: "Custom Design",
            desc: "Tailored landscape plans"
          }, {
            icon: Activity,
            title: "Full Cleanup",
            desc: "Seasonal debris removal"
          }].map((feature, i) => <div key={i} data-index={i} className="flex flex-col items-center text-center gap-3">
                <div className="w-12 h-12 rounded-md bg-accent flex items-center justify-center border border-border/30 mb-2 shadow-sm">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-default font-medium text-foreground tracking-wide">{feature.title}</h3>
                <p className="text-sm text-muted-foreground font-default">{feature.desc}</p>
              </div>)}
          </div>
        </div>

      </div>
    </section>;
}